import React, { useEffect, useState } from 'react';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { AdminService } from '../../services/adminService';
import { useToast } from '../../contexts/ToastContext';
import PasswordSetupModal from './PasswordSetupModal';

const AdminInvitationHandler: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const { showToast } = useToast();

  useEffect(() => {
    const handleInvitation = async () => {
      try {
        // Check if this is a valid email link
        if (!isSignInWithEmailLink(auth, window.location.href)) {
          setError('Invalid invitation link');
          setLoading(false);
          return;
        }

        // Get email from URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const email = urlParams.get('email');
        if (!email) {
          setError('Invalid invitation link: missing email parameter.');
          setLoading(false);
          return;
        }

        // Set flag to prevent AuthContext from checking authorization during invitation acceptance
        // This flag is time-limited and cleared immediately after acceptance
        localStorage.setItem('adminInvitationInProgress', 'true');

        // Sign in with the email link
        const result = await signInWithEmailLink(auth, email, window.location.href);
        const user = result.user;

        // Verify the signed-in user's email matches the invitation email
        if (user.email?.toLowerCase() !== email.toLowerCase()) {
          localStorage.removeItem('adminInvitationInProgress');
          setError('Email mismatch: the signed-in account does not match the invitation email.');
          setLoading(false);
          return;
        }

        // Check if this email has a pending invitation
        const pendingInvitation = await AdminService.findPendingInvitationByEmail(email);

        if (!pendingInvitation) {
          setError('No pending invitation found for this email');
          setLoading(false);
          return;
        }

        // Check if invitation has expired
        if (pendingInvitation.expiresAt && new Date(pendingInvitation.expiresAt) < new Date()) {
          setError('This invitation has expired');
          setLoading(false);
          return;
        }

        // Accept the invitation and store the uid
        if (!pendingInvitation.id) {
          throw new Error('Invalid invitation: missing ID');
        }
        await AdminService.acceptAdminInvitation(
          pendingInvitation.id,
          user.displayName || '',
          user.photoURL || '',
          user.uid
        );

        // Clear the bypass flag immediately after acceptance
        localStorage.removeItem('adminInvitationInProgress');

        // Force reload auth token to trigger AuthContext re-check
        await user.reload();
        await user.getIdToken(true);

        showToast('Welcome! Your admin access has been granted.', 'success');

        // Show password setup modal for newly accepted users
        setUserEmail(email);
        setShowPasswordSetup(true);
        setLoading(false);

        // Clear the URL
        window.history.replaceState({}, document.title, window.location.pathname);

      } catch (error: any) {
        console.error('Error processing admin invitation:', error);
        // Clear the flag if there's an error
        localStorage.removeItem('adminInvitationInProgress');
        setError(`Failed to process invitation: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    handleInvitation();
  }, [showToast]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div role="status" aria-live="polite" className="max-w-md w-full space-y-8 p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto"></div>
            <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white font-['Manrope']">
              Processing Invitation
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 font-['Manrope']">
              Please wait while we set up your admin access...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="max-w-md w-full space-y-8 p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto">
              <svg aria-hidden="true" className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white font-['Manrope']">
              Invitation Error
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 font-['Manrope']">
              {error}
            </p>
            <div className="mt-6">
              <button
                onClick={() => window.location.href = '/'}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handlePasswordSetupComplete = () => {
    setShowPasswordSetup(false);
    // Redirect to dashboard
    window.location.reload();
  };

  const handlePasswordSetupSkip = () => {
    setShowPasswordSetup(false);
    // Redirect to dashboard
    window.location.reload();
  };

  return (
    <>
      {showPasswordSetup && (
        <PasswordSetupModal
          isOpen={showPasswordSetup}
          onClose={handlePasswordSetupSkip}
          onSuccess={handlePasswordSetupComplete}
          userEmail={userEmail}
        />
      )}
    </>
  );
};

export default AdminInvitationHandler;